import { useNavigate, useLocation } from 'react-router-dom'
import { useSession } from '../lib/session'
import { usePlatform } from '../lib/platform'
import TabBar from './TabBar'
import MiniPlayer from './MiniPlayer'
import ClipStatus from './ClipStatus'

/** `onHome` lets a screen own what the wordmark does. Only Auth needs it: it
 *  holds its step in local state rather than in the URL, so navigating to "/"
 *  while already on "/" would leave it sitting on the code screen.
 *
 * `hidePlayer` suppresses the now-playing bar without touching playback — the
 * `<audio>` element lives in PlayerProvider, above the router, so a track
 * left playing keeps playing underneath. The song-writing wizard sets this:
 * the bar sits at the same height as the wizard's own primary button and its
 * error/status text, and on a phone screen those collide.
 */
export function Shell({ children, back, title, tabs, onHome, hidePlayer }) {
  const { user, church, role, isSuperadmin } = useSession()
  const nav = useNavigate()
  const { device } = usePlatform()

  // On a phone, `tabs` is a per-screen choice (the wizard and sub-flows opt
  // out to get the space back). On desktop there's room for a permanent rail
  // regardless, and staff screens need one — People/Process/Inbox were only
  // ever reachable from each other via back-links; a rail is the real fix,
  // not a per-screen prop that would have to be threaded through all of them.
  const showRail = device === 'desktop' && !!user
  const showNav = tabs || showRail

  /**
   * The wordmark goes home. Two rules keep it from becoming a way in:
   *
   * 1. The destination is the literal string "/" — never a value read from the
   *    query string, localStorage, or the profile. There is nothing here for an
   *    open-redirect to latch onto, and no full-page navigation to poison.
   * 2. "/" is <Gate/>, which re-derives the landing spot from the *session* on
   *    every visit, and the <Only> guards still run on whatever it picks. The
   *    button can't reach a screen the current session isn't entitled to; a
   *    signed-out visitor gets the landing, and that's the whole branch.
   */
  const goHome = () => (onHome ? onHome() : nav('/'))

  const { pathname } = useLocation()

  /**
   * The topbar carries two controls, not five.
   *
   * It used to hold Mod / Profile / Day / Out — three text pills and one icon,
   * four ways out of the current screen, all the same visual weight, none of
   * them the one you wanted. Signing out in particular sat one mis-tap from
   * the profile button with no confirmation behind it.
   *
   * Now: notifications, because that's the one ambient thing worth a glance
   * from anywhere; and the person, because that is the single door to
   * everything about the account. Day/night lives on /account with the rest
   * of the settings. Sign out, moderation, and deletion live behind that
   * account door too, where they have room for labels.
   */
  const onProfile = pathname === '/me'

  return (
    <div className={`app${showNav ? ' has-tabs' : ''}`}>
      <div className="topbar">
        <button type="button" className="mark" onClick={goHome}
                aria-label={user ? 'Beseter — home' : 'Beseter — back to the start'}>
          Beseter
        </button>
        {/* Two facts, not one string. The church role comes from
            church_members and is the only thing that grants anything; the
            superadmin flag comes from users.role and is not scoped to a
            church at all. Rendering only the former meant a signed-in
            superadmin's topbar read "songwriter", which is true of their
            membership and misleading about who they are. Staff is shown as a
            separate mark rather than replacing the membership line, because
            both are still true and the church one is what the rest of the app
            is actually gating on. */}
        {user && (church || isSuperadmin) && (
          <span className="who">
            {church && <span className="who-org">{church.name}</span>}
            <span className="who-role">
              {church && <span className="who-cap">{role}</span>}
              {isSuperadmin && <span className="who-staff">Superadmin</span>}
            </span>
          </span>
        )}
        {user && (
          <button className="iconbtn icon-only" onClick={() => nav('/notifications')}
                  aria-label="Notifications">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9.6a6 6 0 0 1 12 0c0 4 1.4 5.4 2 6.2H4c.6-.8 2-2.2 2-6.2Z" />
              <path d="M10 18.6a2.1 2.1 0 0 0 4 0" />
            </svg>
          </button>
        )}
        {user && (
          <button className={`avatarbtn is-glyph${onProfile ? ' is-current' : ''}`}
                  onClick={() => nav('/me')}
                  aria-label="You and your settings"
                  aria-current={onProfile ? 'page' : undefined}>
            {/* person.crop.circle. The bust is clipped BY the circle rather
                than floating inside it — that clip is the whole difference
                between this symbol and the generic "user in a ring". The
                button's own border is dropped (.avatarbtn.is-glyph) so the
                symbol's circle isn't doubled by a second ring around it. */}
            <svg viewBox="0 0 24 24" width="27" height="27" fill="none"
                 stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
                 aria-hidden="true">
              <clipPath id="pcc"><circle cx="12" cy="12" r="9.15" /></clipPath>
              <circle cx="12" cy="12" r="9.15" />
              <g clipPath="url(#pcc)">
                <circle cx="12" cy="9.7" r="3.1" />
                <path d="M5.3 20.8a6.85 6.85 0 0 1 13.4 0" />
              </g>
            </svg>
          </button>
        )}
      </div>
      <div className="screen">
        {back && <button className="backlink" onClick={() => nav(back)}>‹ {title || 'Back'}</button>}
        {children}
      </div>
      {/* Above the tab bar, outside .screen — it must not scroll with content,
          and it shows on every shell whether or not that shell has tabs. */}
      {!hidePlayer && <MiniPlayer />}
      <ClipStatus />
      {showNav && <TabBar />}
    </div>
  )
}
